"use client";

import type { CSSProperties } from "react";
import { Clock, Copy, PiggyBank, TrendingUp } from "lucide-react";

import { formatPercentRaw, formatWholeNumber } from "@/components/services/seo/seo-revenue-projection";
import { CcButton } from "@/components/ui/CcButton";

import type { AiAutomationHubspotSubpageRoiResultsCopy } from "./ai-automation-page-copy";
import type { HubSpotRoiProjection } from "./hubspot-roi-projection";

type CcHubSpotRoiResultsPanelProps = {
  copy: AiAutomationHubspotSubpageRoiResultsCopy;
  money: Intl.NumberFormat;
  result: HubSpotRoiProjection;
  shareUrl: string | null;
  shareCopied: boolean;
  onCopyShareLink: () => void;
};

function BreakdownBar({ fraction }: { fraction: number }) {
  return (
    <span className="cc-hubspot-roi-results__bar-track" aria-hidden="true">
      <span
        className="cc-hubspot-roi-results__bar-fill"
        style={{ "--cc-hubspot-roi-bar": Math.max(0.04, fraction) } as CSSProperties}
      />
    </span>
  );
}

/** HubSpot ROI calculator — flat Automators-style results column (no collapsibles). */
export function CcHubSpotRoiResultsPanel({
  copy,
  money,
  result,
  shareUrl,
  shareCopied,
  onCopyShareLink,
}: CcHubSpotRoiResultsPanelProps) {
  const locale = copy.locale;

  return (
    <div className="cc-hubspot-roi-results cc-traffic__result" aria-live="polite">
      <h3 className="cc-hubspot-roi-results__title">{copy.resultsTitle}</h3>

      {result.kind === "value" ? (
        (() => {
          const { kpis } = result;
          const breakdownTotal = Math.max(1, kpis.labourSavings + kpis.errorReductionSavings);
          const labourFraction = kpis.labourSavings / breakdownTotal;
          const errorFraction = kpis.errorReductionSavings / breakdownTotal;

          return (
            <>
              <div className="cc-hubspot-roi-results__cards">
                <article className="cc-hubspot-roi-results__card cc-hubspot-roi-results__card--muted">
                  <Clock className="cc-hubspot-roi-results__card-icon" aria-hidden="true" />
                  <p className="cc-hubspot-roi-results__card-label">{copy.hoursSavedLabel}</p>
                  <p className="cc-hubspot-roi-results__card-value">
                    {formatWholeNumber(kpis.annualHoursSaved, locale)}
                  </p>
                  <p className="cc-hubspot-roi-results__card-sub">{copy.hoursSavedSubtext}</p>
                </article>

                <article className="cc-hubspot-roi-results__card cc-hubspot-roi-results__card--primary">
                  <PiggyBank className="cc-hubspot-roi-results__card-icon" aria-hidden="true" />
                  <p className="cc-hubspot-roi-results__card-label">{copy.annualSavingsLabel}</p>
                  <p className="cc-hubspot-roi-results__card-value">
                    {money.format(kpis.annualCostSavings)}
                  </p>
                  <p className="cc-hubspot-roi-results__card-sub">{copy.annualSavingsSubtext}</p>
                </article>

                <div className="cc-hubspot-roi-results__duo">
                  <article className="cc-hubspot-roi-results__card cc-hubspot-roi-results__card--muted cc-hubspot-roi-results__card--compact">
                    <TrendingUp className="cc-hubspot-roi-results__card-icon" aria-hidden="true" />
                    <p className="cc-hubspot-roi-results__card-label">{copy.roiLabel}</p>
                    <p className="cc-hubspot-roi-results__card-value">
                      {formatPercentRaw(kpis.roiPercentage, money.resolvedOptions().locale)}%
                    </p>
                    <p className="cc-hubspot-roi-results__card-sub">{copy.roiSubtext}</p>
                  </article>
                  <article className="cc-hubspot-roi-results__card cc-hubspot-roi-results__card--muted cc-hubspot-roi-results__card--compact">
                    <Clock className="cc-hubspot-roi-results__card-icon" aria-hidden="true" />
                    <p className="cc-hubspot-roi-results__card-label">{copy.paybackLabel}</p>
                    <p className="cc-hubspot-roi-results__card-value">
                      {copy.paybackValue.replace("{n}", String(kpis.paybackMonths))}
                    </p>
                    <p className="cc-hubspot-roi-results__card-sub">{copy.paybackSubtext}</p>
                  </article>
                </div>
              </div>

              <div className="cc-hubspot-roi-results__breakdown">
                <p className="cc-hubspot-roi-results__section-label">{copy.breakdownTitle}</p>
                <div className="cc-hubspot-roi-results__breakdown-panel">
                  <div className="cc-hubspot-roi-results__breakdown-row">
                    <div className="cc-hubspot-roi-results__breakdown-head">
                      <span>{copy.breakdownLabourLabel}</span>
                      <span className="cc-hubspot-roi-results__breakdown-amount">
                        {money.format(kpis.labourSavings)}
                      </span>
                    </div>
                    <BreakdownBar fraction={labourFraction} />
                  </div>
                  <div className="cc-hubspot-roi-results__breakdown-row">
                    <div className="cc-hubspot-roi-results__breakdown-head">
                      <span>{copy.breakdownErrorLabel}</span>
                      <span className="cc-hubspot-roi-results__breakdown-amount">
                        {money.format(kpis.errorReductionSavings)}
                      </span>
                    </div>
                    <BreakdownBar fraction={errorFraction} />
                  </div>
                </div>
              </div>
            </>
          );
        })()
      ) : (
        <p className="cc-traffic__empty">{copy.incompleteNote}</p>
      )}

      <div className="cc-hubspot-roi-results__cta">
        <CcButton
          bookCallPopup
          bookCallVariant="hubspot"
          label={copy.resultsCta}
          expand
          expandCompact
          className="cc-hubspot-roi-results__cta-btn"
        />
        {shareUrl ? (
          <button
            type="button"
            className="cc-hubspot-roi-results__share"
            onClick={onCopyShareLink}
          >
            <Copy className="cc-hubspot-roi-results__share-icon" aria-hidden="true" />
            {shareCopied ? copy.shareLinkCopied : copy.shareLinkLabel}
          </button>
        ) : null}
      </div>

      <p className="cc-traffic__footnote">{copy.footnote}</p>
    </div>
  );
}
